import { useRouter } from 'next/router';
import { useEffect, useContext, useState, useRef } from 'react';
import { Button, FormControl, IconButton, Popper, TextField, Tooltip } from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import QaIcon from '@material-ui/icons/BuildSharp';
import { makeStyles } from '@material-ui/core/styles';
import { useHotkeys } from 'react-hotkeys-hook';

import { useAllUsers } from '../utils/users';
import { onError } from '../utils/errorLib';
import { AppContext } from './AppState';
import { useAuth } from '../utils/useAuth';
import Kbd from './Kbd';


const useStyles = makeStyles((theme) => ({
  formControl: {
    margin: theme.spacing(1),
    minWidth: 120,
    maxWidth: 400,
    backgroundColor: theme.palette.accent.main,
  },
  qaButton: {
    marginTop: theme.spacing(1.5),
    marginLeft: theme.spacing(0.5),
    height: '40px',
  },
  qaIconOpen: {
    marginTop: '12px',
    marginRight: '20px',
    marginLeft: '10px',
    color: theme.palette.secondary.main,
    width: '40px',
    height: '40px',
  },
  qaIconClosed: {
    marginRight: '20px',
    color: theme.palette.secondary.light,
    width: '40px',
    height: '40px',
  },
  qaIconRed: {
    borderStyle: 'solid',
    borderWidth: '1px',
    borderColor: '#f44336',
  },
}));

export default function QaToolbar() {
  const classes = useStyles();
  const router = useRouter();

  const { allUserItems } = useAllUsers();

  const [ prevGalleryUser, setPrevGalleryUser ] = useState(null);
  const [ allUsersList, setAllUsersList ] = useState(null);
  const [ selectedUserValue, setSelectedUserValue ] = useState(null);
  const [ isActingAsUser, setIsActingAsUser ] = useState(false);
  const actAsUserRef = useRef(null);
  const {
    isAccountLoaded,
    setCredits,
    setServiceCredits,
    setDiscount,
    setSkipQa,
    setSelected,
    setTilingEditorAvailable,
    setUserStitchingAvailable,
    setImportAvailable,
    qa, qaDisplay, setQaDisplay,
    galleryUser, setGalleryUser,
    setRedirect,
    setFolder,
  } = useContext(AppContext);

  const { isAuthenticated, user } = useAuth();

  useEffect(() => {
    if (isAuthenticated && user && qa && allUserItems) {
      try {
        // Sort users by domain and then email username.
        const collator = new Intl.Collator(undefined, { sensitivity: 'base' });
        let allUserItemsSorted = allUserItems.sort(
          (first, second) => {
            let [firstFront, firstBack] = first.email.split('@');
            let [secondFront, secondBack] = second.email.split('@');
            return (collator.compare(firstBack + '@' + firstFront, secondBack + '@' + secondFront));
          }
        );
        // Move current user to the front of the list.
        let [currentUser] = allUserItemsSorted.filter(item => item.userId === user.sub);
        allUserItemsSorted = allUserItemsSorted.filter(item => item.userId !== user.sub);
        allUserItemsSorted.unshift(currentUser);
        setAllUsersList(allUserItemsSorted);
      } catch (e) {
        onError(e);
      }
    }
  }, [allUserItems, isAuthenticated, qa, user]);

  useEffect(() => {
    async function onGalleryUser() {
      if (allUsersList && galleryUser && isAccountLoaded) {
        // FIXME: Avoid O(n) search
        for (var i = 0; i < allUsersList.length; i++) {
          const userItem = allUsersList[i];
          if (userItem.userId === galleryUser) {
            setIsActingAsUser(user && galleryUser != user.sub);
            setSelectedUserValue({ title: userItem.email, userId: userItem.userId });

            // Keep in sync with components/AppState.js userItem useEffect
            setCredits(userItem.credits);
            const sc = userItem['creditsService'];
            setServiceCredits(sc ? sc : 0);
            setDiscount(userItem.discount);
            setSkipQa(true);
            setTilingEditorAvailable(true);
            setUserStitchingAvailable(true);
            setImportAvailable(true);

            if (prevGalleryUser !== galleryUser) {
              // If changing users (not just updating user data), reset selection and current folder
              setSelected([]);
              setFolder('');
              setPrevGalleryUser(galleryUser);
            }

            break;
          }
        }
      }
    }
    onGalleryUser();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    allUsersList, isAccountLoaded, galleryUser, user, setCredits, setServiceCredits, setDiscount, setSkipQa,
    setTilingEditorAvailable, setUserStitchingAvailable, setImportAvailable
  ]);

  async function handleUserChange(event, newValue) {
    if (newValue !== null && galleryUser !== newValue.userId) {
      setGalleryUser(newValue.userId);
      if (['/view', '/report', '/upload', '/import'].includes(router.pathname)) {
        // If act-as user changes while viewing or creating a material, return to the gallery view
        setRedirect('/home');
      }
    }
  }

  async function toggleQaDisplay() {
    setQaDisplay(!qaDisplay);
  }

  const PopperWide = function (props) {
    return (<Popper {...props} style={{ width: 450 }} placement='bottom-start' />)
  }

  // Keyboard shortcut: Alt+U to focus "Act as User" dropdown
  useHotkeys('Alt+u', () => {
    if (qa && qaDisplay && actAsUserRef.current) {
      const input = actAsUserRef.current.querySelector('input');
      if (input) {
        input.focus();
        input.select();
      }
    }
  });
  // Keyboard shortcut: Alt+Q to toggle QA UI
  useHotkeys('alt+q', () => {
    if (qa) {
      toggleQaDisplay();
    }
  });
  if (qa) {
    return (<>
      {qaDisplay && allUsersList && (<>
        <FormControl className={classes.formControl} ref={actAsUserRef}>
          <Autocomplete
            options={allUsersList.slice(0).map((item) => ({
              title: item.email,
              domain: item.email.split('@')[1],
              userId: item.userId,
            }))}
            getOptionLabel={(option) => option.title}
            getOptionSelected={(option, value) => option.userId === value.userId}
            value={selectedUserValue}
            onChange={handleUserChange}
            noOptionsText='No matching users'
            groupBy={(option) => option.domain}
            PopperComponent={PopperWide}
            style={{ width: 400 }}
            className={classes.selectUser}
            renderInput={
              (params) => <TextField {...params} error={isActingAsUser} label='Act as User' fullWidth />
            }
          />
        </FormControl>
        <Button
          variant='outlined'
          className={classes.qaButton}
          color='secondary'
          size='small'
          onClick={() => setRedirect('/qa')}
        >
          QA Scans
        </Button>
      </>)}
      <Tooltip title={<>Toggle QA UI <Kbd>Alt</Kbd> + <Kbd>Q</Kbd></>}>
        <IconButton
          className={`
            ${qaDisplay ? classes.qaIconOpen : classes.qaIconClosed}
            ${user && galleryUser != user.sub ? classes.qaIconRed : ''}
          `}
          onClick={toggleQaDisplay}
        >
          <QaIcon />
        </IconButton>
      </Tooltip>
    </>);
  } else {
    return <></>;
  }
}
